import { ChangeDetectionStrategy, Component, HostBinding, computed, input } from '@angular/core';

import { imageHelper } from 'lib/helpers/image-helper.class.js';
import { stringHelper } from 'lib/helpers/string-helper.class.js';
import { CoreComponent } from 'lib/ui/core/core.component.js';
import { DialogService } from 'lib/ui/dialog/index.js';
import { CssStyles, IImage } from 'lib/ui/ui-models.js';
import { appVersion } from 'src/app/appVersion.js';

type ImageMode = 'NgOptimizedImage' | 'avatar';
type ImageBoxShadow = 'default' | 'featured';
export type ImageSize = 'original' | '50px' | '250px';

type OptimizedImage = {
	readonly src: string;
	readonly title: string | undefined;
	readonly width: number;
	readonly height: number;
	readonly alt: string;
	readonly priority: boolean;
	readonly isResponsive: boolean;
	readonly styles: CssStyles;
};

type AvatarImage = {
	readonly size: number;
	readonly name: string;
	readonly styles: CssStyles;
};

type ImageRadiusType =
	| 'none'
	| 'full'
	| 'top'
	| 'bottom'
	| 'left'
	| 'right'
	| 'topRight'
	| 'topLeft'
	| 'bottomLeft'
	| 'bottomRight'
	| 'bottomLeftAndTopRight';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'lib-image',
	templateUrl: 'image.component.html',
	styleUrls: ['image.component.scss'],
	standalone: false
})
export class ImageComponent extends CoreComponent {
	/**
	 * Currently the version hash is not required because all images have unique url and are never replaced
	 */
	private readonly addVersionHash: boolean = false;

	public readonly image = input.required<IImage | undefined>();
	public readonly imageSize = input.required<ImageSize>();
	public readonly alt = input.required<string>();

	public readonly maxHeight = input<number>();
	public readonly maxWidth = input<number>();
	public readonly dialogPreviewImage = input<IImage | undefined>();
	public readonly isRound = input<boolean>(this.roundImages());
	public readonly priority = input<boolean>(false);
	public readonly isResponsive = input<boolean>(false);
	public readonly imageId = input<string>();
	public readonly avatarName = input<string>();
	public readonly borderRadius = input<number>(10);
	public readonly radiusType = input<ImageRadiusType>('full');
	public readonly boxShadow = input<ImageBoxShadow>('default');

	@HostBinding('style.display') blockStyle: string = 'block';

	private readonly imageTitle = computed<string>(() => {
		const avatarName = this.avatarName();

		if (avatarName) {
			return avatarName;
		}
		return this.alt();
	});

	private readonly calculatedImageMode = computed<ImageMode>(() => {
		if (!this.image() && this.avatarName()) {
			return 'avatar';
		}
		return 'NgOptimizedImage';
	});

	protected readonly optimizedImage = computed<OptimizedImage | undefined>(() => {
		const imageMode = this.calculatedImageMode();

		if (imageMode !== 'NgOptimizedImage') {
			return undefined;
		}

		const image = this.image();

		if (!image) {
			return undefined;
		}

		const imageSize = this.imageSize();
		const finalizedUrl = this.getImageSrc(image, imageSize);
		const maxHeight = this.maxHeight();
		const maxWidth = this.maxWidth();

		let maxWidthToUse: number | undefined;
		let maxHeightToUse: number | undefined;

		let calculatedWidth: number = image.originalImage.width;
		let calculatedHeight: number = image.originalImage.height;

		if (imageSize === '50px') {
			maxHeightToUse = 50;
			maxWidthToUse = 50;
		} else if (imageSize === '250px') {
			maxHeightToUse = 250;
			maxWidthToUse = 250;
		}

		if (maxHeight) {
			maxHeightToUse = maxHeight;
		}

		if (maxWidth) {
			maxWidthToUse = maxWidth;
		}

		// calculate max height
		if (maxHeightToUse && calculatedHeight && calculatedWidth) {
			const calculatedSize = imageHelper.getDimensionsForMaxHeight(
				{
					height: calculatedHeight,
					width: calculatedWidth
				},
				maxHeightToUse
			);

			calculatedHeight = calculatedSize.height;
			calculatedWidth = calculatedSize.width;
		}
		// calculate max width if needed
		if (maxWidthToUse && calculatedHeight && calculatedWidth) {
			const calculatedSize = imageHelper.getDimensionsForMaxWidth(
				{
					height: calculatedHeight,
					width: calculatedWidth
				},
				maxWidthToUse
			);

			calculatedHeight = calculatedSize.height;
			calculatedWidth = calculatedSize.width;
		}

		if (!finalizedUrl || !calculatedWidth || !calculatedHeight) {
			return undefined;
		}

		return {
			src: finalizedUrl,
			title: this.imageTitle(),
			height: calculatedHeight,
			width: calculatedWidth,
			styles: this.getBorderRadiusStyle(),
			alt: this.alt(),
			priority: this.priority(),
			isResponsive: this.isResponsive()
		};
	});

	protected readonly avatarImage = computed<AvatarImage | undefined>(() => {
		const imageMode = this.calculatedImageMode();

		if (imageMode !== 'avatar') {
			return undefined;
		}

		const avatarName = this.avatarName();

		if (!avatarName) {
			return undefined;
		}

		const imageSize = this.imageSize();
		const maxHeight = this.maxHeight();
		const maxWidth = this.maxWidth();

		let calculatedSize: number = 50;

		if (maxHeight) {
			calculatedSize = maxHeight;
		} else if (maxWidth) {
			calculatedSize = maxWidth;
		} else {
			if (imageSize === '50px') {
				calculatedSize = 50;
			} else if (imageSize === '250px') {
				calculatedSize = 250;
			}
		}

		return {
			name: avatarName,
			styles: this.getBorderRadiusStyle(),
			size: calculatedSize
		};
	});

	constructor(private dialogService: DialogService) {
		super();
	}

	openOptimizedImageDialog(event: Event, optimizedImage: OptimizedImage): void {
		const dialogPreviewImage = this.dialogPreviewImage();

		if (!dialogPreviewImage) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();

		this.dialogService.openImageDialog({
			image: dialogPreviewImage,
			title: optimizedImage.title
		});
	}

	private getImageSrc(image: IImage, imageSize: ImageSize): string | undefined {
		let finalizedUrl: string = image.originalImage.absoluteUrl;

		if (imageSize === '50px') {
			finalizedUrl = image.teaser100.absoluteUrl;
		} else if (imageSize === '250px') {
			finalizedUrl = image.teaser250.absoluteUrl;
		}

		if (this.addVersionHash) {
			finalizedUrl += `${this.getParamSeparator(
				finalizedUrl ?? ''
			)}v=${stringHelper.replaceNonAlphanumericExceptUnderscore(appVersion.version, '-')}}`;
		}

		return finalizedUrl;
	}

	private getParamSeparator(src: string): string {
		if (src.includes('?')) {
			return '&';
		}
		return '?';
	}

	private getBorderRadiusStyle(): CssStyles {
		const radiusType = this.radiusType();
		const borderRadius = this.borderRadius();

		if (this.isRound()) {
			return {
				borderRadius: `7px`
			};
		}

		if (radiusType == 'none') {
			return {};
		}

		if (radiusType === 'top') {
			return {
				borderTopLeftRadius: `${borderRadius}px`,
				borderTopRightRadius: `${borderRadius}px`
			};
		}

		if (radiusType === 'bottom') {
			return {
				borderBottomLeftRadius: `${borderRadius}px`,
				borderBottomRightRadius: `${borderRadius}px`
			};
		}

		if (radiusType === 'left') {
			return {
				borderTopLeftRadius: `${borderRadius}px`,
				borderBottomLeftRadius: `${borderRadius}px`
			};
		}

		if (radiusType === 'right') {
			return {
				borderTopRightRadius: `${borderRadius}px`,
				borderBottomRightRadius: `${borderRadius}px`
			};
		}

		if (radiusType === 'topRight') {
			return {
				borderTopRightRadius: `${borderRadius}px`
			};
		}

		if (radiusType === 'topLeft') {
			return {
				borderTopLeftRadius: `${borderRadius}px`
			};
		}

		if (radiusType === 'bottomRight') {
			return {
				borderBottomRightRadius: `${borderRadius}px`
			};
		}

		if (radiusType === 'bottomLeft') {
			return {
				borderBottomLeftRadius: `${borderRadius}px`
			};
		}

		if (radiusType === 'bottomLeftAndTopRight') {
			return {
				borderBottomLeftRadius: `${borderRadius}px`,
				borderTopRightRadius: `${borderRadius}px`
			};
		}

		return {
			borderRadius: `${borderRadius}px`
		};
	}
}
